import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ChevronDown, ChevronRight, Lock, Globe, MoreVertical, X, ImagePlus } from 'lucide-react';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import Header from '../../../shared/components/layout/Header';
import Layout from '../../components/layout/AppLayout';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import {
  getBlockByID,
  getProjectImages,
  postProjectImagesBulkUpdate,
  uploadFilesToS3,
  postUnitProjectImages
} from '../../services/api/implementation/impl';

const IMAGE_TABS = [
  { id: 'Private', label: 'Private', Icon: Lock },
  { id: 'Public', label: 'Public', Icon: Globe }
];

const layoutCls = (collapsed) =>
  collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]';

const BlockUnitImages = () => {
  const { id: projectId, blockId } = useParams();
  const { collapsed } = useSidebar();
  const blockIdNum = blockId ? parseInt(blockId, 10) : null;

  const [blockData, setBlockData] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(IMAGE_TABS[0].id);
  const [expandedUnits, setExpandedUnits] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [imagesRefreshKey, setImagesRefreshKey] = useState(0);
  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);
  const [addImagesUnitId, setAddImagesUnitId] = useState('');
  const [addImagesFiles, setAddImagesFiles] = useState([]);
  const [addImagesVisibility, setAddImagesVisibility] = useState('private');
  const [isUploadingUnitImages, setIsUploadingUnitImages] = useState(false);
  const addImagesFileInputRef = useRef(null);
  const [unitImageOrder, setUnitImageOrder] = useState({});
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const block = blockData;
  const units = block?.units ?? [];

  useEffect(() => {
    if (!blockIdNum || !projectId) {
      setBlockData(null);
      setImages([]);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      getBlockByID(blockIdNum),
      getProjectImages(projectId, 1, 500)
    ])
      .then(([blockRes, imagesRes]) => {
        if (cancelled) return;
        if (!blockRes) {
          setBlockData(null);
          setImages([]);
          return;
        }
        const levels = blockRes.levels ?? [];
        const unitsFromApi = levels.flatMap((level) => level.units ?? []).map((u) => ({
          id: u.id,
          name: u.unitName ?? `Unit ${u.id}`
        }));
        setBlockData({
          id: blockRes.id,
          name: blockRes.name ?? `Block ${blockRes.id}`,
          units: unitsFromApi
        });

        const list = Array.isArray(imagesRes?.data) ? imagesRes.data : [];
        const projectIdNum = parseInt(projectId, 10);
        // Unit images: (project_id + unit_id) OR (project_id + unit_id + block_id); for this block show unit_id set and block either this block or null
        const forBlock = list.filter(
          (item) =>
            item.project_id === projectIdNum &&
            item.unit_id != null &&
            (item.block_id == null || item.block_id === blockIdNum)
        );
        setImages(
          forBlock.map((item) => ({
            id: item.id,
            name: item.name,
            url: item.url,
            visibility: item.visibility,
            tag: (item.visibility || 'public').toLowerCase(),
            title: item.name,
            block_id: item.block_id,
            unit_id: item.unit_id,
            project_id: item.project_id,
            order: item.order
          }))
        );
        setUnitImageOrder({});
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || err?.message || 'Failed to load block');
          setBlockData(null);
          setImages([]);
          toast.error('Failed to load block and images');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [blockIdNum, projectId, imagesRefreshKey]);

  const handleAddImagesFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const valid = files.filter((f) => imageTypes.includes(f.type));
    if (valid.length < files.length) toast.error('Only image files (JPEG, PNG, GIF, WebP, BMP) are allowed.');
    setAddImagesFiles((prev) => [...prev, ...valid]);
    if (addImagesFileInputRef.current) addImagesFileInputRef.current.value = '';
  };

  const removeAddImagesFile = (index) => {
    setAddImagesFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeAddImagesModal = () => {
    setAddImagesModalOpen(false);
    setAddImagesUnitId('');
    setAddImagesFiles([]);
    setAddImagesVisibility('private');
    if (addImagesFileInputRef.current) addImagesFileInputRef.current.value = '';
  };

  const handleAddImagesSubmit = async () => {
    if (!addImagesUnitId || addImagesFiles.length === 0 || !projectId || !blockIdNum) return;
    const unitIdNum = Number(addImagesUnitId);
    const currentCount = getOrderedUnitImages(unitIdNum).length;
    setIsUploadingUnitImages(true);
    try {
      const uploaded = await uploadFilesToS3(addImagesFiles);
      if (uploaded?.length > 0) {
        await postUnitProjectImages({
          images: uploaded.map((f, idx) => ({
            name: f.name,
            url: f.url,
            project_id: Number(projectId),
            block_id: blockIdNum,
            unit_id: unitIdNum,
            visibility: addImagesVisibility,
            order: currentCount + idx + 1
          }))
        });
        toast.success(`${uploaded.length} image(s) added to unit.`);
        closeAddImagesModal();
        setImagesRefreshKey((k) => k + 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload images');
    } finally {
      setIsUploadingUnitImages(false);
    }
  };

  const getUnitImages = (unitId) =>
    images.filter(
      (img) =>
        img.unit_id === unitId &&
        (img.tag === (activeTab === 'Private' ? 'private' : 'public'))
    );

  const getOrderedUnitImages = (unitId) => {
    const list = getUnitImages(unitId);
    const orderIds = unitImageOrder[unitId];
    if (orderIds?.length) {
      const idToImg = new Map(list.map((img) => [img.id, img]));
      const ordered = orderIds.map((id) => idToImg.get(id)).filter(Boolean);
      const appended = list.filter((img) => !orderIds.includes(img.id));
      return [...ordered, ...appended];
    }
    return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  const allFilteredImages = units.flatMap((u) =>
    getOrderedUnitImages(u.id).map((img) => ({ ...img, unitId: u.id }))
  );
  const filteredIds = allFilteredImages.map((img) => img.id);

  const handleUnitImagesDragEnd = async (result, unitId) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const ordered = getOrderedUnitImages(unitId);
    const reordered = Array.from(ordered);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    const newOrderIds = reordered.map((img) => img.id);
    setUnitImageOrder((prev) => ({ ...prev, [unitId]: newOrderIds }));
    setIsSavingOrder(true);
    try {
      await postProjectImagesBulkUpdate({
        images: reordered.map((img, idx) => ({
          id: img.id,
          visibility: img.tag ?? img.visibility ?? 'private',
          order: idx + 1
        }))
      });
      toast.success('Order saved.');
    } catch (err) {
      setUnitImageOrder((prev) => ({ ...prev, [unitId]: ordered.map((i) => i.id) }));
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save order');
    } finally {
      setIsSavingOrder(false);
    }
  };
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleUnit = (unitId) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const expandAll = () => setExpandedUnits(new Set(units.map((u) => u.id)));
  const collapseAll = () => setExpandedUnits(new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...filteredIds]));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  useEffect(() => {
    setSelectedIds((prev) => new Set([...prev].filter((id) => filteredIds.includes(id))));
  }, [activeTab, filteredIds.length]);

  const setImageTag = async (imageId, tag) => {
    try {
      await postProjectImagesBulkUpdate({ images: [{ id: imageId, visibility: tag }] });
      setImages((prev) =>
        prev.map((img) => (img.id === imageId ? { ...img, tag, visibility: tag } : img))
      );
      setOpenMenuId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
      toast.success(`Image set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  const bulkSetTag = async (tag) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await postProjectImagesBulkUpdate({
        images: ids.map((id) => ({ id, visibility: tag }))
      });
      setImages((prev) =>
        prev.map((img) => (ids.includes(img.id) ? { ...img, tag, visibility: tag } : img))
      );
      setSelectedIds(new Set());
      toast.success(`${ids.length} image(s) set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className={`fixed top-0 z-[100] transition-all duration-300 ease-in-out bg-white shadow-sm ${layoutCls(collapsed)}`}>
          <Header data={{ name: 'Sales > Block & Units Images > Unit Images' }} />
        </div>
        <div className="mt-[4rem] flex items-center justify-center py-16">
          <Loader isVisible message="Loading block and units..." />
        </div>
      </Layout>
    );
  }

  if (!block && !loading) {
    return (
      <Layout>
        <div className={`fixed top-0 z-[100] transition-all duration-300 ease-in-out bg-white shadow-sm ${layoutCls(collapsed)}`}>
          <Header data={{ name: 'Sales > Block & Units Images > Unit Images' }} />
        </div>
        <div className="mt-[4rem] px-6 py-8">
          <p className="text-stone-600">{loadError || 'Block not found.'}</p>
        </div>
      </Layout>
    );
  }

  const totalFilteredCount = allFilteredImages.length;

  return (
    <Layout>
      {/* Header — high z-index so it stays above scrolling content */}
      <div className={`fixed top-0 z-[100] transition-all duration-300 ease-in-out bg-white shadow-sm ${layoutCls(collapsed)}`}>
        <Header data={{ name: `Sales > Block & Units Images > ${block.name}` }} />
      </div>

      {/* Single toolbar: back, tabs, title, actions — one row */}
      <div className={`fixed top-[4rem] z-[90] bg-white border-b border-stone-100 transition-all duration-300 ${layoutCls(collapsed)}`}>
        <div className="flex flex-nowrap items-center justify-between gap-3 px-6 py-2">
          <div className="flex flex-nowrap items-center gap-3 min-w-0 shrink-0">
            <div className="flex rounded-xl bg-stone-100 p-1 shrink-0">
              {IMAGE_TABS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab === id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  <Icon size={14} className="shrink-0" />
                  {label}
                </button>
              ))}
            </div>
            <h2 className="text-sm font-semibold text-stone-800 whitespace-nowrap shrink-0">
              Unit images · {block.name} {activeTab === 'Private' ? '(Private)' : '(Public)'}
            </h2>
          </div>
          <div className="flex flex-nowrap items-center gap-3 text-sm shrink-0">
            {units.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setAddImagesModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 shrink-0"
                >
                  <ImagePlus size={18} />
                  Add images
                </button>
                <span className="flex items-center gap-2 text-stone-500">
                  <button type="button" onClick={expandAll} className="font-medium text-stone-600 hover:text-stone-800">
                    Expand all
                  </button>
                  <span className="text-stone-300">|</span>
                  <button type="button" onClick={collapseAll} className="font-medium text-stone-600 hover:text-stone-800">
                    Collapse all
                  </button>
                </span>
              </>
            )}
            <span className="text-stone-500 tabular-nums">
              {units.length} unit{units.length !== 1 ? 's' : ''} · {totalFilteredCount} image{totalFilteredCount !== 1 ? 's' : ''}
            </span>
            {totalFilteredCount > 0 && (
              <label className="flex items-center gap-2 cursor-pointer select-none text-stone-600">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  className="rounded border-stone-300 text-primary focus:ring-primary"
                />
                Select all
              </label>
            )}
          </div>
        </div>

        {/* Bulk actions (in flow of toolbar) */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-6 py-2 bg-primary/5 border-t border-primary/10">
            <span className="text-sm font-medium text-stone-700">{selectedIds.size} selected</span>
            {activeTab === 'Private' && (
              <button
                type="button"
                onClick={() => bulkSetTag('public')}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 shadow-sm"
              >
                <Globe size={16} />
                Make Public
              </button>
            )}
            {activeTab === 'Public' && (
              <button
                type="button"
                onClick={() => bulkSetTag('private')}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 shadow-sm"
              >
                <Lock size={16} />
                Make Private
              </button>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 ml-auto shadow-sm"
            >
              <X size={16} />
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Content — margin increases when bulk actions bar is visible */}
      <DragDropContext
        onDragEnd={(result) => {
          if (!result.destination) return;
          const unitId = result.source?.droppableId?.replace(/^unit-/, '');
          if (unitId) handleUnitImagesDragEnd(result, parseInt(unitId, 10));
        }}
      >
      <div
        className={`overflow-auto bg-stone-50/80 px-6 pb-8 pt-6 ${
          selectedIds.size > 0 ? 'mt-[13rem] min-h-[calc(100vh-13rem)]' : 'mt-[10rem] min-h-[calc(100vh-10rem)]'
        }`}
      >
        <div className="w-full space-y-4">
          {units.length === 0 ? (
            <div className="rounded-xl border border-stone-200 bg-white py-12 text-center">
              <p className="text-stone-500">No units in this block.</p>
            </div>
          ) : (
          units.map((unit) => {
            const isExpanded = expandedUnits.has(unit.id);
            const unitImagesFiltered = getUnitImages(unit.id);
            const unitImagesOrdered = getOrderedUnitImages(unit.id);
            return (
              <div key={unit.id} className="rounded-xl border border-stone-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => toggleUnit(unit.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50/80 transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown size={22} className="text-stone-500 shrink-0" />
                  ) : (
                    <ChevronRight size={22} className="text-stone-500 shrink-0" />
                  )}
                  <span className="font-semibold text-stone-800">{unit.name}</span>
                  <span className="text-sm text-stone-500">
                    {unitImagesFiltered.length} image{unitImagesFiltered.length !== 1 ? 's' : ''}
                  </span>
                </button>
                {isExpanded && (
                  <div className="border-t border-stone-100 bg-stone-50/50 px-5 pb-5 pt-4">
                    {unitImagesFiltered.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-stone-200 bg-white/80 py-12 text-center">
                        <p className="text-sm text-stone-500">No {activeTab.toLowerCase()} images in this unit</p>
                      </div>
                    ) : (
                        <Droppable droppableId={`unit-${unit.id}`} direction="horizontal">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
                            >
                              {unitImagesOrdered.map((img, index) => (
                                <Draggable key={img.id} draggableId={`img-${img.id}`} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      className={`relative rounded-xl border-2 bg-white transition-all duration-200 ${
                                        selectedIds.has(img.id)
                                          ? 'border-primary shadow-md ring-2 ring-primary/20'
                                          : 'border-stone-200 hover:border-stone-300 hover:shadow-md'
                                      } ${dragSnapshot.isDragging ? 'opacity-90 shadow-lg ring-2 ring-primary/30' : ''}`}
                                    >
                                      <div className="aspect-square bg-stone-100 relative overflow-hidden">
                                        <img
                                          src={img.url}
                                          alt={img.title ?? img.name ?? 'Unit image'}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/300x300?text=Image';
                                          }}
                                        />
                                      </div>
                                      <div className="flex items-center gap-2 p-3 border-t border-stone-100 bg-white relative">
                                        <label className="flex items-center shrink-0 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={selectedIds.has(img.id)}
                                            onChange={() => toggleSelect(img.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="rounded border-stone-300 text-primary focus:ring-primary w-4 h-4"
                                          />
                                        </label>
                                        <p className="text-sm font-medium text-stone-700 truncate min-w-0 flex-1" title={img.title ?? img.name}>
                                          {img.title ?? img.name}
                                        </p>
                                        <div className="relative shrink-0" ref={openMenuId === img.id ? menuRef : null}>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenMenuId(openMenuId === img.id ? null : img.id);
                                            }}
                                            className="p-1.5 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                                            aria-label="Options"
                                          >
                                            <MoreVertical size={18} />
                                          </button>
                                          {openMenuId === img.id && (
                                            <div
                                              className="absolute right-0 top-full mt-1 py-1.5 w-48 bg-white rounded-xl border border-stone-200 shadow-xl z-[80]"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {img.tag === 'private' && (
                                                <button
                                                  type="button"
                                                  onClick={() => setImageTag(img.id, 'public')}
                                                  className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                                                >
                                                  <Globe size={16} />
                                                  Make Public
                                                </button>
                                              )}
                                              {img.tag === 'public' && (
                                                <button
                                                  type="button"
                                                  onClick={() => setImageTag(img.id, 'private')}
                                                  className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                                                >
                                                  <Lock size={16} />
                                                  Make Private
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                    )}
                  </div>
                )}
              </div>
            );
          })
          )}
        </div>
      </div>
      </DragDropContext>

      {addImagesModalOpen && (
        <Modal
          isDirty={addImagesFiles.length > 0 || !!addImagesUnitId}
          confirmOnClose={true}
          name="Add unit images"
          width={45}
          onClose={closeAddImagesModal}
        >
          <div className="flex flex-col gap-4 p-2">
            <div>
              <label className="mb-1 block font-medium text-stone-700">Unit</label>
              <select
                value={addImagesUnitId}
                onChange={(e) => setAddImagesUnitId(e.target.value)}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-800 focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="">Select unit</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-medium text-stone-700">Visibility</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="addImagesVisibility"
                    checked={addImagesVisibility === 'private'}
                    onChange={() => setAddImagesVisibility('private')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Lock size={16} className="text-stone-500" />
                  Private
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="addImagesVisibility"
                    checked={addImagesVisibility === 'public'}
                    onChange={() => setAddImagesVisibility('public')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Globe size={16} className="text-stone-500" />
                  Public
                </label>
              </div>
            </div>
            <div>
              <label className="mb-1 block font-medium text-stone-700">Images</label>
              <input
                ref={addImagesFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                multiple
                className="hidden"
                onChange={handleAddImagesFileChange}
              />
              <button
                type="button"
                onClick={() => addImagesFileInputRef.current?.click()}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
              >
                <ImagePlus size={18} />
                Select images
              </button>
              {addImagesFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {addImagesFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5 text-sm"
                    >
                      <span className="truncate max-w-[180px]" title={file.name}>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAddImagesFile(index)}
                        className="p-0.5 rounded hover:bg-stone-200 text-stone-500"
                        aria-label="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <button
                type="button"
                onClick={closeAddImagesModal}
                className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddImagesSubmit}
                disabled={!addImagesUnitId || addImagesFiles.length === 0 || isUploadingUnitImages}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isUploadingUnitImages ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default BlockUnitImages;
